// In src/SceneOutliner.jsx
import React from 'react';
import { motion } from 'framer-motion';
import {
    Trash2, Eye, EyeOff, HelpCircle, Ruler, Square, Sigma, Maximize,
    MessageSquare, Mountain, Scissors, Cloud, Camera as CameraIcon
} from 'lucide-react';
import { getIconForOutlinerObject } from './tool-definitions';
import { useTranslation } from 'react-i18next';

const OutlinerItem = ({ obj, isSelected, onSelect, onDelete }) => {
    const [isVisible, setIsVisible] = React.useState(obj.visible);

    const handleVisibilityToggle = (e) => {
        e.stopPropagation(); // Prevent the item from being selected
        
        const newVisibility = !obj.visible;
        
        obj.visible = newVisibility;
        
        setIsVisible(newVisibility);

        //    If this object is a volume, also toggle its clipping status.
        if (obj.constructor.name === 'BoxVolume') {
            obj.clip = newVisibility;
        }
    };

    const displayName = obj.title || obj.name;

    return (
        <motion.li
            className={`outliner-item ${isSelected ? 'active' : ''}`}
            onClick={() => onSelect(obj)}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
        >
            <span className="outliner-item-icon">{getIconForOutlinerObject(obj)}</span>
            <span className="outliner-item-name">{displayName}</span>
            <div className="outliner-item-actions">
                <button onClick={handleVisibilityToggle} title="Toggle Visibility">
                    {isVisible ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <button onClick={(e) => { e.stopPropagation(); onDelete(obj); }} title="Delete">
                    <Trash2 size={14} />
                </button>
            </div>
        </motion.li>
    );
};


export default function SceneOutliner({ groupedObjects, selectedObjectUUID, onSelect, onDelete, t }) {

    const categoryLabels = {
        pointclouds: t("scenePanel.outliner.categories.pointclouds"),
        measurements: t("scenePanel.outliner.categories.measurements"),
        volumes: t("scenePanel.outliner.categories.volumes"),
        profiles: t("scenePanel.outliner.categories.profiles"),
        annotations: t("scenePanel.outliner.categories.annotations"),
        other: t("scenePanel.outliner.categories.other")
    };
    
    const categories = Object.keys(groupedObjects);

    return (
        <div className="scene-outliner">
            {categories.length === 0 ? (
                <div className="outliner-empty">{t("scenePanel.outliner.empty")}</div>
            ) : (
                categories.map(category => (
                    <details key={category} open>
                        <summary>{categoryLabels[category]}</summary>
                        <ul>
                            {groupedObjects[category].map((obj, index) => (
                                <OutlinerItem
                                    key={obj.uuid || index}
                                    obj={obj}
                                    // The comparison is now a stable string comparison
                                    isSelected={selectedObjectUUID === obj.uuid}
                                    onSelect={onSelect}
                                    onDelete={onDelete}
                                />
                            ))}
                        </ul>
                    </details>
                ))
            )}
        </div>
    );
}